import type { PublicTokenRegistryItem } from '@arhs-fleetback/types/entities/public-tokens';
import { createSelector } from 'reselect';

import type { MayBe } from 'modules/global/typing/common/common';
import { getSelectedGroupId } from 'modules/groups/selectors';
import type { PublicTokenReducer } from 'modules/publicToken/reducer';
import type { PublicTokenDashboardTarget } from 'modules/publicToken/types';
import { isGroupManagement } from 'modules/router/selectors';
import type { RootState } from 'reducers/index';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps, getRoot } from 'utils/selectors';

const getState = (state: RootState): PublicTokenReducer => state.publicToken;

const getDataState = createSelector(getState, state => state.data);

const getDataIds = createSelector(getDataState, state => state.ids);

export const getDataEntities = createSelector(getDataState, state => state.entities);

export const getPublicTokenDashboardTarget = createSelector(
  getSelectedDealerId,
  getSelectedGroupId,
  isGroupManagement,
  (dealerId, groupId, isGroup): PublicTokenDashboardTarget => (isGroup ? { groupId } : { groupId, dealerId })
);

export const getPublicTokenById = createSelector(
  getDataEntities,
  getProps<string>,
  (entities, publicTokenId): MayBe<PublicTokenRegistryItem> => entities[publicTokenId]
);

export const getPublicTokenByIds = createSelector(getDataEntities, getProps<string[]>, (entities, publicTokenIds) =>
  publicTokenIds.reduce<PublicTokenRegistryItem[]>((acc, publicTokenId) => {
    const publicToken = entities[publicTokenId];
    if (publicToken) {
      acc.push(publicToken);
    }
    return acc;
  }, []));

const getUIState = createSelector(getState, state => state.ui);

export const isLoading = createSelector(getUIState, state => state.loading);

export const isCreating = createSelector(getUIState, state => state.creating);

export const isDeleting = createSelector(getUIState, getProps<string>, ({ deleting: deletingState }, publicTokenId) =>
  deletingState.includes(publicTokenId));

export const isPatching = createSelector(getUIState, getProps<string>, ({ patching: patchingState }, publicTokenId) =>
  patchingState.includes(publicTokenId));

export const getPublicTokenEntries = createSelector(getRoot, getDataIds, getPublicTokenByIds);

export const getAllTokenNames = createSelector(getPublicTokenEntries, allTokens => allTokens.map(({ name }) => name));
